// @ts-expect-error - does not have a typescript declaration file :(
import html2pdf from 'html2pdf.js';

import { configSettings } from './config';
import * as Sentry from '@sentry/react';
import { queryClient } from '@/queries';
import { QUERY_KEYS } from './constants';
import { LogLevel, SentryLogOptions, User } from '@/types';
import Cookies from 'js-cookie';
import { appNotify } from './appNotify';

const { logger } = Sentry;

export function downloadHtmlAsPDF(htmlContent: string, fileName: string) {
    html2pdf().from(htmlContent).save(fileName);
}

// tags is 'any' so that we can force 'feature' tag if tags are used, but still have a default.
// We want to force 'feature' for future use (nice dashboards).
export function logWithSentry(
    message: string,
    { tags = {} as any, context = {}, level = 'error' }: SentryLogOptions = {}
) {
    Sentry.withScope(scope => {
        // Always add user_id
        tags['user_id'] = queryClient.getQueryData<User>([QUERY_KEYS.USER])?.user_id;

        Object.entries(tags).forEach(([key, value]) => {
            scope.setTag(key, value);
        });
        scope.setContext('Details', context);
        switch (level) {
            case 'info':
                Sentry.captureMessage(message, 'info');
                break;
            case 'warning':
                Sentry.captureMessage(message, 'warning');
                break;
            case 'error':
                Sentry.captureMessage(message, 'error');
                break;
            default:
                Sentry.captureMessage(message, 'error');
                break;
        }
    });

    const logLevelToConsoleMethod: Record<LogLevel, keyof Console> = {
        info: 'info',
        warning: 'warn', // 'warning' must map to 'warn'
        error: 'error',
        log: 'log',
    };
    // all this does is console log on local env
    if (configSettings.environment !== 'staging' && configSettings.environment !== 'production') {
        const method = logLevelToConsoleMethod[level];

        // Any is used here because there isn't an easy way type this so that consoleFn works lol
        // eslint-disable-next-line no-console
        const consoleFn = console[method] as (...args: any[]) => void;

        if (typeof consoleFn === 'function') {
            consoleFn(`[${logger}] ${message}`, context);
        }
    }
}

export async function copyToClipboard(textToCopy: string, successCallback = () => {}, failCallback = () => {}) {
    try {
        await navigator.clipboard.writeText(textToCopy);
        return successCallback();
    } catch (err) {
        console.error('Failed to copy text: ', err);
    }

    // Fallback
    const textarea = document.createElement('textarea');
    textarea.value = textToCopy;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'absolute';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();

    let success = false;
    try {
        success = document.execCommand('copy');
    } catch (err) {
        console.error('Failed to copy text using execCommand: ', err);
    }

    document.body.removeChild(textarea);

    if (success) {
        return successCallback();
    }
    return failCallback();
}

export function getUUID(): string {
    const lut: string[] = Array(256);
    for (let i = 0; i < 256; i++) {
        lut[i] = (i < 16 ? '0' : '') + i.toString(16);
    }

    const getHex = (num: number): string => {
        const hex = lut[num & 0xff];
        if (!hex) throw new Error(`Invalid index in LUT: ${num & 0xff}`);
        return hex;
    };

    const d0 = (Math.random() * 0xffffffff) | 0;
    const d1 = (Math.random() * 0xffffffff) | 0;
    const d2 = (Math.random() * 0xffffffff) | 0;
    const d3 = (Math.random() * 0xffffffff) | 0;

    return (
        getHex(d0) +
        getHex(d0 >> 8) +
        getHex(d0 >> 16) +
        getHex(d0 >> 24) +
        '-' +
        getHex(d1) +
        getHex(d1 >> 8) +
        '-' +
        getHex(((d1 >> 16) & 0x0f) | 0x40) +
        getHex(d1 >> 24) +
        '-' +
        getHex((d2 & 0x3f) | 0x80) +
        getHex(d2 >> 8) +
        '-' +
        getHex(d2 >> 16) +
        getHex(d2 >> 24) +
        getHex(d3) +
        getHex(d3 >> 8) +
        getHex(d3 >> 16) +
        getHex(d3 >> 24)
    );
}

export function getSessionUUID() {
    // We save session for 10 minutes and renew every time there's an event to be tracked
    const sessionUuid = Cookies.get('session_uuid') || getUUID();
    const in10Minutes = 1 / 24 / 6; // js-cookies uses days as base unit
    Cookies.set('session_uuid', sessionUuid, { expires: in10Minutes });
    Cookies.set('session_uuid', sessionUuid, { domain: 'stanwith.me', expires: in10Minutes });
    Cookies.set('session_uuid', sessionUuid, { domain: 'stan.store', expires: in10Minutes });
    return sessionUuid;
}

export function createCenteredPopupWin(
    pageURL: string,
    pageTitle: string,
    popupWinWidth: number,
    popupWinHeight: number
) {
    const left = (screen.width - popupWinWidth) / 2;
    const top = (screen.height - popupWinHeight) / 4;

    const authPopupWindow = window.open(
        pageURL,
        pageTitle,
        `resizable=yes, width=${popupWinWidth}, height=${popupWinHeight}, top=${top}, left=${left}`
    );

    if (!authPopupWindow) {
        appNotify({
            text: 'Please allow popups for this website!',
            type: 'error',
        });
    }
    return authPopupWindow;
}
